import { styled, TypographyProps } from '@mui/material';

import { useMemo } from 'react';

import {
  Box,
  Button,
  ButtonsContainer,
  Modal,
  Typography,
  useModalWrapperContext,
} from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';
import { CrownIcon } from '@app-velocity/icons';

import { SubscriptionButton } from '../../../components/SubscriptionButton';
import { BuyPackButton } from './components/BuyPackButton';

const StyledDescription = styled(Box)({
  textAlign: 'center',
});

const StyledTitle = styled(Typography)<
  TypographyProps & { component: React.ElementType }
>(({ theme }) => ({
  fontSize: 18,
  marginBottom: theme.spacing(2),
  textAlign: 'center',
}));

const StyledText = styled(Typography)({
  color: 'var(--text-text-secondary-color)',
  whiteSpace: 'pre-line',
});

const IconContainer = styled('div')({
  textAlign: 'center',
  svg: {
    stroke: 'var(--button-button-primary-color)',
  },
});

const isNoMoreCreditsModalData = (
  data: unknown
): data is {
  role: 'OWNER' | 'MEMBER' | 'ADMIN';
  tier: 'FREE' | 'REGULAR' | 'PRO' | 'ENTERPRISE';
  teamId: string;
} => {
  const castedData = data as {
    role: 'OWNER' | 'MEMBER' | 'ADMIN';
    tier: 'FREE' | 'REGULAR' | 'PRO' | 'ENTERPRISE';
    teamId: string;
  };

  return !!castedData.role;
};

export const NoMoreCreditsLeftModalContent = () => {
  const { t } = useI18n();
  const { modalData, openedModalId, closeModal } = useModalWrapperContext();

  const { role, tier, teamId } = useMemo(() => {
    const currentModalData = modalData[openedModalId];

    if (isNoMoreCreditsModalData(currentModalData)) {
      return currentModalData;
    }

    return { role: null, tier: null, teamId: null };
  }, [modalData, openedModalId]);

  return (
    <Modal data-testid="no-more-credits-modal">
      <Modal.Content>
        <IconContainer>
          <CrownIcon />
        </IconContainer>
        <StyledDescription>
          <StyledTitle variant="header" component="h2">
            {t('users:no_more_credits.title')}
          </StyledTitle>
          <StyledText variant="body2">
            {t('users:no_more_credits.description')}
          </StyledText>
          {role !== 'OWNER' ? (
            <StyledText variant="body2">
              {t('users:no_more_credits.ask_owner')}
            </StyledText>
          ) : null}
        </StyledDescription>
        <ButtonsContainer direction="vertical">
          {tier && ['REGULAR', 'PRO'].includes(tier) && role === 'OWNER' ? (
            <BuyPackButton teamId={teamId} />
          ) : null}

          {tier && ['FREE'].includes(tier) && role === 'OWNER' ? (
            <SubscriptionButton />
          ) : null}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => closeModal()}
          >
            {t('shared:close')}
          </Button>
        </ButtonsContainer>
      </Modal.Content>
    </Modal>
  );
};
